<template>
<!-- eslint-disable max-len -->
<span>
  <transition enter-active-class="animated slideInDown" leave-active-class="animated fadeOut">
    <div class="report-container" id="report-container">
      <div :class="`report ${pdf ? 'pdf' : ''}`" ref="report" id="report">
        <div
          v-for="(sections, p) in displayedSectionDecisionPages"
          :key="`page${p}`"
          class="report-page"
        >
          <div :class="`report-header ${pdf ? 'pdf' : ''}`">
            <!-- title -->
            <div class="report-title" v-html="narrativeText"></div>
          </div>

          <!-- name/date -->
          <div class="report-row">
            <div class="no-wrap r-col-1">
              Email:
            </div>
            <div class="no-wrap r-col-2">
              {{email}}
            </div>
          </div>
          <div class="report-row">
            <div class="no-wrap r-col-1">
              Name:
            </div>
            <div class="no-wrap r-col-2">
              {{username}}
            </div>
          </div>
          <div class="report-row">
            <div class="no-wrap r-col-1">
              Date:
            </div>
            <div class="no-wrap r-col-2">
              {{date}}
            </div>
          </div>
          <div class="report-row">
            <div class="no-wrap r-col-1">
              Points:
            </div>
            <div class="no-wrap r-col-2">
              {{ correctCount }} of {{ decisionCount }}
            </div>
          </div>
          <div class="report-row">
            <div class="no-wrap r-col-1">
              Percent:
            </div>
            <div class="no-wrap r-col-2">
              {{totalScore}}%
            </div>
          </div>

          <!-- table of decisions by section -->
          <div class="report-table">
            <div class="report-table-row table-header">
              <div class="no-wrap rt-col-1">
                Major area/Minor area
              </div>
              <div class="no-wrap rt-col-2">
                Points
              </div>
              <div class="no-wrap rt-col-3">
                Percent
              </div>
              <div class="no-wrap rt-col-4">
                Response
              </div>
            </div>
            <div
              v-for="(section, s) in sections"
              :key="`section${s}`"
              class="report-table-section"
            >
              <div class="report-table-row">
                <div class="no-wrap rt-col-1">
                  {{section.name}}
                </div>
                <div class="no-wrap rt-col-2">
                  {{section.correctCount}} of {{section.decisions.length}}
                </div>
                <div class="no-wrap rt-col-3">
                  {{section.score.toFixed(0)}}%
                </div>
                <div class="no-wrap rt-col-4">
                  &nbsp;
                </div>
              </div>
                <div
                  v-for="(decision, d) in section.decisions"
                  :key="`decision${d}`"
                  class="report-table-row"
                >
                <div class="no-wrap rt-col-1">
                  &nbsp;&nbsp;&nbsp;&nbsp;{{ decision.displayName }}
                </div>
                <div class="no-wrap rt-col-2">
                  {{ decision.isCorrect ? '1' : '0' }} of 1
                </div>
                <div class="no-wrap rt-col-3">
                  &nbsp;
                </div>
                <div class="no-wrap rt-col-4">
                  {{ decision.displayAnswer }}
                </div>
              </div>
            </div>
          </div>

          <!-- footer -->
          <div
            v-if="pdf && (activeEnvironment.reportFooter || activeEnvironment.reportFooterLogo)"
            style="
              background-color: black;
              color: white;
              padding: 4px 50px;
              width: 100%;
              min-height: 36px;
              display: flex;
              flex-flow: row nowrap;
              justify-content: space-between;
              align-items: flex-start;
            "
          >
            <!-- footer message (e.g. displaying ©) -->
            <div v-html="activeEnvironment.reportFooter"></div>
            <!-- footer logo -->
            <img
              v-if="activeEnvironment.reportFooterLogo"
              :src="resolveAssetPath(activeEnvironment.reportFooterLogo, 'ui/customer')"
              :class="`footer-logo ${pdf ? 'pdf' : ''}`"
              ref="footerLogo"
            >
          </div>
        </div>
      </div>
    </div>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <div v-if="showDownloading" class="download-message">Downloading!</div>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <button v-if="showNextButton"
      class="btn download-button"
      @click.prevent="onClickDownload"
    >Download</button>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <bottom-bar v-if="showNextButton" :next="showNarratorNotification ? onComplete : undefined" :tabindex="3" ref="bottomBar" :noBar="true">
      <span slot="button">
        <span v-html="(narrative && narrative.nextButtonText) ? narrative.nextButtonText : ((game && game.nextButtonDefaultText) ? game.nextButtonDefaultText : 'Next')"></span>
      </span>
    </bottom-bar>
  </transition>
</span>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import JsPDF from 'jspdf';
import domtoimage from 'dom-to-image-more';

import { iOS, isSafari } from '../../../utils/platform-detection';

import Narrator from '../ui/Narrator';
import NarratorNotification from '../ui/NarratorNotification';
import BottomBar from '../ui/BottomBar';

export default {
  // $emit 'input' on complete
  components: {
    Narrator,
    NarratorNotification,
    BottomBar,
  },
  data() {
    return {
      showNextButton: false,
      showDownloading: false,
      showNarrator: false,
      showNarratorNotification: false,
      pdf: false,
      /** which PDF page is currently visible. if -1, all pages are visible. */
      pdfPage: -1,
      maxTableRowsPerPage: 45,
    };
  },
  computed: {
    ...mapGetters([
      'activeEnvironment',
      'activeClass',
      'tooltip',
      'narrative',
      'narrativeText',
      'group',
      'me',
      'game',
      'dateActiveGameCompleted',
      'challenges',
      'challenge'
    ]),
    /** array of sections with: `name`, percentage `score`, `correctCount`, array of completed `decisions` */
    sectionDecisions() {
      if (this.group == null) return [];
      const decisionsBySection = [];

      /* for each object in this.group.challenges,
       * add a section object with _id, name, score, correctCount, decisions
       */
      this.group.challenges.forEach((c) => {
        const section = {
          _id: c._id,
          name: '',
          score: 0,
          correctCount: 0,
          decisions: [],
        };

        /* fill decisions with { displayName, isCorrect, displayAnswer } for each object in
         * this.group.teamDecisions with the matching challenge _id
         */
        this.group.teamDecisions.forEach((d) => {
          if (d.challenge === section._id) {
            const sectionDecision = {
              displayName: '',
              isCorrect: false,
              displayAnswer: '',
            };

            /* determine isCorrect based on whether the metricAdjustments include
             * this.group.metrics[0] being incremented by a positive number.
             * displayName should be the decision title
             * displayAnswer should be the choice title
             */
            const roleDecision = d.decisions[0];
            sectionDecision.displayName = roleDecision ? this.removeTags(roleDecision.title) : '';
            const choice = roleDecision.choices[0];
            sectionDecision.displayAnswer = choice ? this.removeTags(choice.title) : '';
            sectionDecision.isCorrect =
              choice
                ? choice.metricAdjustments.some(adj => adj.metric === this.group.metrics[0]._id && adj.adjustment > 0)
                : false;

            /* aggregate correct count for each section and compute the score */
            if (sectionDecision.isCorrect) section.correctCount += 1;

            section.decisions.push(sectionDecision);
          }
        });

        /* compute section score */
        section.score = section.decisions.length > 0 ? (section.correctCount / section.decisions.length) * 100 : 0;

        /* add section names from vuex store */
        const storeChallenge = this.challenges[section._id];
        if (storeChallenge) {
          const index = storeChallenge.title.indexOf(' - ');
          section.name =
            (index !== -1 && ((index + 3) < storeChallenge.title.length))
              ? storeChallenge.title.substring(index + 3)
              : storeChallenge.title;
        }

        decisionsBySection.push(section);
      });

      /* sort sections by order in this.game.challenges */
      decisionsBySection.sort((a, b) => {
        const orderA = this.game.challenges.findIndex(c => c._id === a._id);
        const orderB = this.game.challenges.findIndex(c => c._id === b._id);
        return orderA - orderB;
      });

      return decisionsBySection;
    },
    /** total decisions */
    decisionCount() {
      let count = 0;
      this.sectionDecisions.forEach((s) => {
        count += s.decisions.length;
      });
      return count;
    },
    /** total count of correct answers */
    correctCount() {
      let count = 0;
      this.sectionDecisions.forEach((s) => {
        count += s.correctCount;
      });
      return count;
    },
    totalScore() {
      return (this.correctCount * 100 / this.decisionCount).toFixed(0);
    },
    /**
     * Create a slice of sectionDecisions for each page to display.
     * NOTE: this currently does not split up a given section between pages.
     */
    sectionDecisionPages() {
      if (this.pdf) {
        // logic to split the sectionDecisions across pages in a way that will fit
        const pages = [[]];
        for (let s = 0; s < this.sectionDecisions.length; ++s) {
          const currentPage = pages[pages.length - 1];
          const currentPageLength = currentPage.reduce((acc, section) => acc + section.decisions.length + 1, 0);
          const currentSection = this.sectionDecisions[s];
          if (
            (currentPageLength === 0)
            || ((currentPageLength + currentSection.decisions.length) <= this.maxTableRowsPerPage)
          ) {
            currentPage.push(currentSection);
          } else {
            pages.push([currentSection]);
          }
        }
        return pages;
      }
      return [this.sectionDecisions];
    },
    /** selects from sectionDecisionPages / sectionDecisions based on pdf / pdfPage */
    displayedSectionDecisionPages() {
      if (this.pdf) {
        return (this.pdfPage === -1)
          ? this.sectionDecisionPages
          : [this.sectionDecisionPages[this.pdfPage]];
      }
      return [this.sectionDecisions];
    },
    email() {
      const {
        email = ''
      } = this.me || {};
      return email;
    },
    username() {
      const {
        firstName = '',
        lastName = '',
      } = this.me || {};
      return firstName || lastName ? `${firstName} ${lastName}` : this.email;
    },
    date() {
      return (this.game.carryOverMetricsFromActive && this.dateActiveGameCompleted != null)
        ? new Date(this.dateActiveGameCompleted).toLocaleString()
        : ((this.challenge && this.challenge.completed)
          ? new Date(this.challenge.completed).toLocaleString()
          : ((this.group && this.group.updated)
            ? new Date(this.group.updated).toLocaleString()
            : new Date(Date.now()).toLocaleString()));
    },
    reportFileName() {
      return (this.activeClass != null && this.activeClass.studentReportFileName != null)
        ? this.activeClass.studentReportFileName
        : (this.activeEnvironment.reportTitle || this.game.reportTitle || 'Report');
    },
  },
  methods: {
    ...mapActions([
      'getMyGroup',
      'stopSounds'
    ]),
    removeTags(text) {
      return text
        ? (text
          .replace(/<(br|center|strong|div|span)(>|\/>| [^>]*>)/g, '') // start/standalone tags
          .replace(/<\/(br|center|strong|div|span)>/g, '')) // end tags
        : '';
    },
    async show() {
      this.showNarrator = true;
      await Vue.nextTick();
      this.stopSounds();
      await new Promise(resolve => setTimeout(resolve, 1250));
      this.showNarratorNotification = true;
      await new Promise(resolve => setTimeout(resolve, 1250));
      await Vue.nextTick();
    },
    async reset() {
      await Vue.nextTick();
      await this.show();
    },
    navigate() {
      this.$refs.bottomBar.navigate();
    },
    async onComplete() {
      if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
        window.dispatchEvent(new Event('click'));
        return;
      }
      this.$emit('input');
    },
    async loadPageData() {
      try {
        const { _id: groupId } = this.group;
        await this.getMyGroup({ groupId });
      } catch (err) {
        if (await this.$router.handleGameError(err)) return;
        this.$router.showGameErrorCritical();
      }
    },
    onBeforePrint(mql) {
      if (mql instanceof Event) {
        this.pdf = true;
        setTimeout(() => {
          this.pdf = false;
        }, 250);
      } else if (mql.matches) {
        this.pdf = true;
        setTimeout(() => {
          this.pdf = false;
        }, 250);
      }
    },
    async onClickDownload() {
      if (this.showDownloading) return;
      this.showDownloading = true;
      // PDF download works in all browsers except pre-chromium Edge
      // (fails at domtoimage.toPng, and no replacement seems viable, see above)
      // fix for Edge (for now): make the page printable, and open the print dialog automatically
      // (in response to domtoimage.toPng or any other part of the autodownload throwing any error)
      // The user can print to PDF or actually print. Bonus is you can print in other browsers too.
      // Edge requires the user to manually set the Scale in the print dialog to 50% for it to fit.
      // Note that this is generally no longer a problem with the new chromium-based Edge.
      this.pdf = true;
      try {
        await new Promise((resolve, reject) => {
          if (this.$refs.footerLogo && this.$refs.footerLogo.complete) { // wait for logo to load
            resolve();
          } else if (this.$refs.footerLogo) {
            this.$refs.footerLogo.addEventListener('load', resolve);
            this.$refs.footerLogo.addEventListener('error', reject);
          } else {
            console.log('No footerLogo ref found');
            resolve();
          }
        });

        const reportToPngFixed = async() => {
          await Vue.nextTick();
          let dataUrl = await domtoimage.toPng(this.$refs.report, { cacheBust: true });
          // redundant calls required to work on Safari... (https://github.com/tsayen/dom-to-image/issues/343)
          const brokenBecauseSafari = iOS() || isSafari();
          if (brokenBecauseSafari) {
            dataUrl = await domtoimage.toPng(this.$refs.report);
            dataUrl = await domtoimage.toPng(this.$refs.report);
          }
          return dataUrl;
        };

        const doc = new JsPDF('p', 'pt', 'a4');
        console.log(this.sectionDecisionPages);
        for (let i = 0; i < this.sectionDecisionPages.length; ++i) {
          this.pdfPage = i;
          await Vue.nextTick();
          const currentPageLength =
            this.sectionDecisionPages[i].reduce((acc, section) => acc + section.decisions.length + 1, 0);
          if (i !== 0) doc.addPage();
          const dataUrl = await reportToPngFixed();
          doc.addImage(dataUrl, 'PNG', 28, 50, 540, 768 * (currentPageLength / this.maxTableRowsPerPage));
        }
        this.pdfPage = -1;

        // NOTE: given no autodownload, there's no soft-lock (explained below), so soft-lock fix commented out
        doc.save(`${this.reportFileName}.pdf`);
        // Before we download the PDF, we have to consider the possibility that the
        // user's settings are set to view the PDF in the browser first when downloading a PDF
        // (instead of actually downloading it). This can be true in many modern browsers.
        // This is a problem because if it opens in the same page, it destroys the Vue app state.
        // This ends up soft-locking the player such that they can't get past the report page.
        // This problem came to my attention when a Firefox update changed the PDF download behavior
        // to default to viewing it in browser (on the same page) when you initiate a download.
        // To fix this while minimally impacting anyone whose browser actually just downloads stuff
        // when a download is initiated, instead first open a new tab, then artificially initiate
        // the download with a bogus DOM hyperlink element that targets the bloburl output by JsPDF,
        // which either downloads the file or loads the blob viewer. If it doesn't load the
        // blob viewer, we autoclose the tab, leaving you back on the report page. If it does load
        // the blob viewer, the user is left in the blob viewer, where they can either:
        // - close it, returning to the original report tab without downloading, where they can
        //   hit print or refresh to save off the report another way, or continue on with the game.
        // - download/print it (which presents a system dialog, and does carry over the expected
        //   default name) (and then close it to return back to the game).
        // eslint-disable-next-line max-len
        // const win = window.open(`${window.location.origin}/downloadplayerreport/${encodeURIComponent(this.reportFileName)}/${encodeURIComponent(doc.output('bloburl', { filename: `${this.reportFileName}.pdf` }))}`, '_blank');
        // if (win == null) {
        //   doc.save(`${this.reportFileName}.pdf`);
        // }

        this.pdf = false;
        this.showDownloading = false;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to download report PDF!', error);
        try {
          this.pdf = true;
          this.pdfPage = -1;
          await Vue.nextTick();
          window.print();
          this.pdf = false;
          this.showDownloading = false;
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Failed to open print dialog!', e);
          this.pdf = false;
          this.showDownloading = false;
          this.pdfPage = -1;
        }
      }
    },
  },
  watch: {
    async narrative(newValue) {
      if (newValue) await this.reset();
    },
  },
  async mounted() {
    console.log(this.group);

    await Vue.nextTick();
    this.stopSounds();

    // polyfill for safari but works for others as well
    const mediaQueryList = (window.matchMedia != null) ? window.matchMedia('print') : null;
    if (mediaQueryList != null && mediaQueryList.addListener != null) {
      mediaQueryList.removeListener(this.onBeforePrint);
      mediaQueryList.addListener(this.onBeforePrint);
    } else {
      // what you'd normally use, if you weren't needing to support Safari:
      window.removeEventListener('beforeprint', this.onBeforePrint);
      window.addEventListener('beforeprint', this.onBeforePrint);
    }

    await this.show();

    await new Promise((resolve, reject) => {
      if (this.$refs.footerLogo && this.$refs.footerLogo.complete) { // wait for logo to load
        resolve();
      } else if (this.$refs.footerLogo) {
        this.$refs.footerLogo.addEventListener('load', resolve);
        this.$refs.footerLogo.addEventListener('error', reject);
      } else {
        console.log('No footerLogo ref found');
        resolve();
      }
    });

    // uncomment to enable autodownload (but re-add soft-lock fix above if you do)
    // await this.onClickDownload();

    this.showNextButton = true;
  },
};
</script>

<style lang="scss" scoped>
@media print {
  body, body * {
    visibility: hidden !important;
    overflow: visible !important;
  }
  #report-container, #report-container * {
    visibility: visible !important;
    overflow: visible !important;
  }
  #report-container {
    left: 8.75% !important;
    top: 0 !important;
    width: 82.5% !important;
    overflow: visible !important;
    z-index: 10 !important;

    // this makes background graphics show up without needing to check the box
    color-adjust: exact !important;
    print-color-adjust: exact !important;
    -webkit-print-color-adjust: exact !important;
  }
}

.no-wrap {
  white-space: nowrap;
}

.download-button {
  position: absolute;
  bottom: 210px;
  right: 0;
  width: 350px;
  font-size: 28px;
  line-height: 55px;
  margin-right: 45px;
  z-index: 100 !important;
  transition: opacity 0.5s ease-in-out;
}

hr {
  border-top: 1px solid #e9ecef;
}
.report {
  background-color: white;
  color: black;
}
// .report.pdf {
//   border: solid;
//   border-width: 3.5px;
//   border-color: var(--primaryColor);
// }
.report-container {
  position: absolute;
  width: 1150px;
  height: 850px;
  top: 20px;
  left: 30px;
  z-index: 1;
  overflow: auto;
}

.report-header {
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  height: 72px;
  padding: 0 50px;
}
.report-title {
  font-size: 20px;
  font-weight: bold;
  white-space: pre-wrap;
  text-align: center;
  margin: auto;
  max-width: 100%;
  max-height: 100%;
  line-height: 1.12;
}

.footer-logo {
  object-fit: contain;
  max-height: 30px;
  max-width: 30%;
  margin-bottom: 15px;
  z-index: 10;
}

.report-page {
  font-size: 18px;
  padding: 16px;
  width: 100%;
}

.report-row {
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;

  .r-col-1 {
    width: 15%;
    text-align: left;
    font-weight: bold;
  }
  .r-col-2 {
    width: 85%;
    text-align: left;
  }
}

.report-table {
  width: 100%;
  margin-top: 16px;
}
.report-table-section {
  width: 100%;
}
.report-table-row {
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  gap: 1%;

  .rt-col-1 {
    width: 59%;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .rt-col-2 {
    width: 5%;
    text-align: right;
  }
  .rt-col-3 {
    width: 7%;
    text-align: right;
  }
  .rt-col-4 {
    width: 25%;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
.report-table-row.table-header {
  background-color: lightgrey;
  font-weight: bold;
  border-top: 1px solid black;
  border-bottom: 3px solid black;
}

// https://stackoverflow.com/a/31633969/10747078
br {
  display: block;
  content: '';
  margin-top: 32px;
}

.slideInDown {
  animation-duration: 1s;
  animation-timing-function: cubic-bezier(0.19, 1, 0.22, 1); // out expo
}
.fadeOutUp {
  animation-duration: 1s;
  animation-timing-function: cubic-bezier(0.55, 0.055, 0.675, 0.19); // in cubic
}
.narrator-notification {
  bottom: 205px;
}

.download-message {
  position: absolute;
  top: 50%;
  left: 25%;
  z-index: 1000;
  border-radius: 15px;
  border: 3px solid black;
  background-color: var(--primaryColor);
  color: white;
  font-size: 30pt;
  padding: 20px;
  font-weight: bold;
}
</style>



// WEBPACK FOOTER //
// src/components/game/narrative/NarrativeReportDecisions.vue